// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {useContext, useEffect, useState, useMemo} from 'react';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import cn from 'classnames';
import styles from './blog.module.less';
import {Category_badge} from './category_badge';
import {Articles_section} from './articles_section';
import {Splash_screen_loader} from '../../../../toolip/pub/ui/splash_screen_loader/splash_screen_loader';
import {use_data_provider} from '../../../../hola/pub/lib/use_data_provider';
import {get_data_id} from '../../pages/oculus.toolip.io/blog';
import {Editor} from '../../../../shared/editor/editor';

interface i_article_data {
    url: string;
    category?: string | string[];
    title?: string;
    description?: string;
    img?: string;
    author?: string;
    min_read?: number;
}

interface i_hero_props {
    main_article: i_article_data;
    popular_articles: i_article_data[];
    blog_title?: string;
}

const Hero = (props: i_hero_props)=>{
    const {theme} = useContext(Settings_context);
    const {main_article, popular_articles, blog_title='Proxy Blog'} = props;
    if (!main_article || !popular_articles.length)
        return null;
    return <div className={styles.hero_container}>
      <div className={cn(theme.container, styles.hero_content)}>
        <div className={styles.blog_title_container}>
          <h4 className={cn(theme.H4, theme.bold_700)}>
            {blog_title}
          </h4>
        </div>
        <div className={styles.hero_sections}>
          <div className={styles.hero_left}>
            <div className={styles.main_article_wrapper}
              onClick={()=>window.location.href = main_article.url}>
              <div className={styles.main_article_image}>
                {main_article.img &&
                  <img src={main_article.img}
                  alt={main_article.title || 'Article'} />
                }
              </div>
              <div className={styles.main_article_content}>
                {main_article.category &&
                  <div className={styles.categories_container}
                    onClick={e=>e.stopPropagation()}>
                    {(Array.isArray(main_article.category) ?
                      main_article.category :
                      [main_article.category]).map((cat, index)=>
                        <Category_badge key={index} category={cat}/>
                    )}
                  </div>
                }
                {main_article.title &&
                  <h5 className={cn(theme.H5, theme.bold_700,
                    styles.main_article_title)}>
                    {main_article.title}
                  </h5>
                }
                {main_article.description &&
                  <p className={cn(theme.p16, theme.reg,
                    styles.main_article_description)}>
                    {main_article.description}
                  </p>
                }
                <div className={styles.meta_container}>
                  <div className={styles.author_logo}>
                    <div className={styles.logo_icon}></div>
                  </div>
                  <p className={cn(theme.p14, theme.med, styles.author,
                    theme.color_n700)}>
                    {main_article.author || 'Oculus'}
                  </p>
                  <span className={styles.dot}></span>
                  <p className={cn(theme.p12, theme.med, styles.min_read)}>
                    {main_article.min_read || 5} min read
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.hero_right}>
            <p className={cn(theme.p16, theme.med, theme.color_n700,
              styles.popular_title)}>
              Most popular articles
            </p>
            <div className={styles.popular_articles_list}>
              {popular_articles.map((article, index)=>
                <div key={index} className={styles.popular_article_wrapper}>
                  <div className={styles.popular_article_card}
                    onClick={()=>window.location.href = article.url}>
                    <div className={styles.popular_article_image}>
                      {article.img &&
                        <img src={article.img}
                          alt={article.title || 'Article'}/>
                      }
                    </div>
                    <div className={styles.popular_article_content}>
                      {article.category &&
                        <div className={styles.categories_container}
                          onClick={e=>e.stopPropagation()}>
                          {(Array.isArray(article.category) ?
                            article.category :
                            [article.category]).map((cat, i)=>
                              <Category_badge key={i} category={cat}/>
                          )}
                        </div>
                      }
                      {article.title &&
                        <p className={cn(theme.p16, theme.bold,
                          theme.color_n700, styles.popular_article_title)}>
                          {article.title}
                        </p>
                      }
                      <div className={styles.meta_container}>
                        <p className={cn(theme.p14, theme.med,
                          theme.color_n700, styles.author)}>
                          {article.author || 'Unknown'}
                        </p>
                        <span className={styles.dot}></span>
                        <p className={cn(theme.p12, theme.med,
                          styles.min_read)}>
                          {article.min_read || 3} min read
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.hero_separator}></div>
    </div>;
};

interface i_blog_props {
    blog_title?: string;
}

export const Blog = (props: i_blog_props)=>{
    const {data} = use_data_provider(get_data_id);
    const {blog_title='Proxy Blog'} = props;
    const [articles, set_articles] = useState<i_article_data[]>(()=>{
        return data?.status == 'done' && data?.result?.data?.posts ?
        data.result.data.posts : [];
    });
    const [loading, set_loading] = useState(!data || data.status !== 'done');
    useEffect(()=>{
        if (data?.status == 'done')
        {
            if (data.result?.data?.posts && articles.length == 0)
                set_articles(data.result.data.posts);
            set_loading(false);
        }
        else
            set_loading(true);
    }, [data, articles.length]);
    const hero_data = useMemo(()=>{
        if (!articles.length)
            return {main_article: null, popular_articles: []};
        return {
            main_article: articles[0],
            popular_articles: articles.slice(1, 4),
        };
    }, [articles]);
    if (loading)
        return <Splash_screen_loader show={true} />;
    return <div className={styles.blog_container}>
      <Hero
        main_article={hero_data.main_article}
        popular_articles={hero_data.popular_articles}
        blog_title={blog_title}
      />
      <Articles_section
        articles={articles}
        title='Explore categories'
      />
    </div>;
};

export const Blog_editor = props=>{
    const [state, set_state] = useState(props.value || {});
    useEffect(()=>{
        if (props.value)
            set_state(props.value);
    }, [props.value]);
    const handle_save = ()=>{
        props.on_change(state);
    };
    return <Editor
      value={state}
      editors={[
        {key: 'blog_title', label: 'Blog Title', type: 'text'},
      ]}
      on_change={v=>set_state(v)}
      on_save={handle_save}
      on_cancel={props.on_cancel}
    />;
};
